<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Link, link } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Col, Container, Row } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import { Calendar } from '@client/components/calendar';
  import { Loader } from '@client/components/loader';

  import { CampaignCell } from '@client/pages/marketing/campaigns/manage/cell-formatters';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';

  import { structureTree } from '@client/utils/folder-tree';

  import { getDateObject } from '@common/utils/dates';

  import { CampaignsModal } from './campaigns-modal';

  const { basepath } = getContext('config');

  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();

  let loaded = false;
  let selectedDate = null;
  let selectedFolders = [];
  let selectedLabels = [];

  let folders = [];

  let queryResult = [];

  let events = {};
  let maxEventCount = 48;
  let eventsThisDay = 0;

  let currentMonth = month;
  let currentYear = year;
  let currentDay = new Date().getDate();
  let campaigns = {};

  const updateEditorialEvents = (selections) => {
    events = {};

    queryResult.forEach((item) => {
      if (!selections || (selections && item?.folderid && selectedFolders.includes(item.folderid))) {
        if (item?.schedule && item?.enableschedule) {
          let startDate = getDateObject(item?.schedule?.startDate);
          let endDate = getDateObject(item?.schedule?.endDate);
          const originalHour = startDate.getHours();

          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const day = d.getDate();
            const month = d.getMonth();
            const year = d.getFullYear();
            const key = `${String(month + 1).padStart(2, '0')}-${year}`;

            if (!events[key]) {
              events[key] = {};
            }

            if (!events[key][day]) {
              events[key][day] = [];
            }

            events[key][day].push({ ...item, originalHour });
          }
        }
      }
    });

    for (const key in events) {
      for (const day in events[key]) {
        events[key][day].sort((a, b) => a.originalHour - b.originalHour);
      }
    }

    setDaysEvents();
  };

  const setDaysEvents = () => {
    const date = selectedDate ? selectedDate : today;
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const key = `${String(month + 1).padStart(2, '0')}-${year}`;

    if (events[key] && events[key][day]) {
      eventsThisDay = events[key][day].length;
      campaigns = {
        [day]: events[key][day] || []
      };
    } else {
      eventsThisDay = 0;
      campaigns = { [day]: [] };
    }
  };

  const updateFolderSelections = (id, files, name, override) => {
    const exists = override ?? selectedFolders.includes(id);

    if (exists) {
      if (selectedFolders.includes(id)) {
        selectedFolders.splice(selectedFolders.indexOf(id), 1);
        selectedLabels.splice(selectedLabels.indexOf(name), 1);
      }
    } else {
      if (!selectedFolders.includes(id)) {
        selectedFolders.push(id);
        selectedLabels.push(name);
      }
    }

    selectedFolders = [...selectedFolders];
    selectedLabels = [...selectedLabels];

    if (files?.length) {
      files.forEach((file) => updateFolderSelections(file.id, file.files, file.name, exists));
    }
  };

  const onFolderSelection = ({ id, files, name }, override) => {
    updateFolderSelections(id, files, name, override);
    updateEditorialEvents(selectedLabels.length);
  };

  const onSelect = (e, file) => {
    e.preventDefault();
    e.stopPropagation();

    onFolderSelection(file);
  };

  const onDayClick = (data) => {
    if (data?.events?.length) {
      selectedDate = data;
    }
  };

  const onTodayClick = () => setDaysEvents();

  const initialize = async () => {
    loaded = false;

    const response = await AssetsService.getAssetFolders();

    folders = [structureTree(response)];
    queryResult = await CampaignsService.getAll();

    updateEditorialEvents();

    loaded = true;
  };

  let hoverTimeout = null;

  const onDayHover = (d, ev) => {
    clearTimeout(hoverTimeout);

    hoverTimeout = setTimeout(() => {
      if (d && ev) {
        currentDay = d;
        campaigns = { [d]: ev };
        eventsThisDay = ev.length;
      }
    }, 200);
  };

  const onMonthChange = (m, y, d, ev) => {
    currentMonth = m;
    currentYear = y;
    currentDay = d;
    campaigns = { [d]: ev };
    eventsThisDay = ev.length;
  };

  onMount(initialize);
</script>

<div class="page editorial">
  <Loader backdrop show={!loaded} />
  <div class="page-header no-bottom-border">
    <Container fluid>
      <Row class="flex-align--center">
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.CAMPAIGNS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EDITORIAL_CALENDAR')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center" style="max-width: 1248px;">
        <Col xs="8" sm="8" md="8">
          <h1 class="page-title pt-1">{$format('label.CAMPAIGN_SCHEDULE')}</h1>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="tabs">
      <div class="tab-list">
        <Link class="tab" to="/marketing/campaigns">{$format('label.CAMPAIGNS')}</Link>
        <Link class="tab active" to="/marketing/campaigns/editorial">
          {$format('label.EDITORIAL')}<span class="hide--mobile">&nbsp;{$format('label.CALENDAR')}</span>
        </Link>
      </div>
    </div>

    <div class="calendar-layout">
      <div class="calendar-column">
        <!-- <FolderTreeDropdown
          bind:folders
          selectable
          selected={selectedFolders}
          bind:selectedLabels
          {onSelect}
          expanded={false}
        /> -->
        <Calendar
          bind:events
          {today}
          {month}
          {year}
          {onDayClick}
          {onMonthChange}
          {onDayHover}
          {onTodayClick}
          {maxEventCount}
        />
        <CampaignsModal bind:selectedDate {maxEventCount} />
        <small class="d-block pt-3 text-muted">{@html $format('label.EDITORIAL_CALENDAR_NOTE')}</small>
      </div>
      <div class="monthly-events">
        <h1>{$format('label.CAMPAIGNS')}</h1>
        {#each Object.entries(campaigns) as [day, items]}
          <div>
            <h3>
              {formatDate(new Date(`${currentMonth + 1}/${day}/${currentYear}`), 'EEEE, MMMM d')}

              {#if eventsThisDay > 0}
                <small class="total-events">
                  ({eventsThisDay}
                  {$format('label.EVENTS')})
                </small>
              {/if}
            </h3>

            <div class="daily-events">
              {#if items.length && items.length < maxEventCount}
                {#each items as event}
                  <div class="daily-event">
                    <CampaignCell bind:row={event} alwaysShowBanner alwaysShowTagBorder />
                  </div>
                {/each}
              {:else}
                <div class="daily-event">
                  {@html $format('label.X_SCHEDULED_CAMPAIGNS', { values: { count: items.length || '0' } })}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>
</div>

<style lang="scss" src="./editorial.scss"></style>
